/**
 * Lazy Firebase Initialization
 *
 * This module defers Firebase loading until it's actually needed,
 * reducing initial bundle size and improving page load performance.
 */

import type { FirebaseApp } from 'firebase/app';
import type { Analytics } from 'firebase/analytics';

// Firebase configuration (loaded from environment variables)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

let firebaseApp: FirebaseApp | null = null;
let analytics: Analytics | null = null;
let initPromise: Promise<FirebaseApp> | null = null;

/**
 * Lazily initialize Firebase app
 * Only loads the Firebase SDK when first called
 */
export async function getFirebaseApp(): Promise<FirebaseApp> {
  if (firebaseApp) {
    return firebaseApp;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = import('firebase/app').then(({ initializeApp }) => {
    firebaseApp = initializeApp(firebaseConfig);
    return firebaseApp;
  });

  return initPromise;
}

/**
 * Lazily get Firebase Analytics
 * Initializes Firebase app if not already done
 */
export async function getFirebaseAnalytics(): Promise<Analytics> {
  if (analytics) {
    return analytics;
  }

  await getFirebaseApp();

  const { getAnalytics } = await import('firebase/analytics');
  analytics = getAnalytics(firebaseApp!);
  return analytics;
}

/**
 * Log a page view event to Firebase Analytics
 * Safe to call even if analytics hasn't loaded yet
 */
export async function logPageView(pageTitle: string): Promise<void> {
  try {
    const analyticsInstance = await getFirebaseAnalytics();
    const { logEvent } = await import('firebase/analytics');
    logEvent(analyticsInstance, 'page_view', { page_title: pageTitle });
  } catch (error) {
    // Silently fail - analytics shouldn't break the app
    console.warn('Failed to log page view:', error);
  }
}
